import React, { useState, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { createBarcode, deleteBarcode } from "../../../graphql/mutations";
import { updateInventoryItem } from "../../../graphql/inventory/custom-mutations";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { QrBarcodeScanner } from "../../../components/common/QrBarcodeScanner/QrBarcodeScanner";
import { beep } from "../../../utils/utils";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteIcon } from "../../../components/common/iconography";
import { BarcodesStatus } from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { useNotify } from "react-admin";

const useStyles = makeStyles((theme) => ({
  scrollableContainer: {
    maxHeight: "40vh",
    overflowY: "auto",
  },
  dialogPaper: {
    borderRadius: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
  },
  scanButton: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

export const BarcodeScanDialog = ({
  open,
  onClose,
  onSave,
  inventoryItem,
  currentInventory,
  allBarcodes,
  setAllBarcodes,
}) => {
  const classes = useStyles();
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const smUp = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [tempBarcode, setTempBarcode] = useState("");
  const removeModeFlagRef = useRef(false);
  const notify = useNotify();

  const createAndAddBarcode = async ({ value, isScanned }) => {
    const scannedBy = window?.appUser?.fullName;
    const hasInventoryItemId = !!inventoryItem?.id;

    const newBarcode = {
      active: "true",
      groups: window?.appUser?.groups,
      status: hasInventoryItemId ? BarcodesStatus.PLACED : BarcodesStatus.NEW,
      reloadYardId: inventoryItem.location?.reloadYardId || null,
      value: value.trim(),
      scannedIn: scannedBy,
      isScanned,
      inboundShipmentId: currentInventory.inboundShipmentId,
      inventoryId: currentInventory.id,
      locationId: inventoryItem?.locationId,
      ...(hasInventoryItemId && { inventoryItemId: inventoryItem.id }),
    };

    try {
      const result = await API.graphql(
        graphqlOperation(createBarcode, { input: newBarcode })
      );
      const savedBarcode = result.data.createBarcode;
      setAllBarcodes((prev) => {
        const updated = [savedBarcode, ...prev];
        if (hasInventoryItemId) {
          const updatedQuantity = updated.length;
          API.graphql(
            graphqlOperation(updateInventoryItem, {
              input: {
                id: inventoryItem.id,
                quantityOnHand: updatedQuantity,
              },
            })
          );
        }

        return updated;
      });
    } catch (err) {
      logError("BarcodeScanDialog:createAndAddBarcode", err, notify);
    }
  };

  const handleSuccess = async (scannedValue) => {
    const isInRemoveMode = removeModeFlagRef.current;

    if (isInRemoveMode) {
      beep(300, 700, 200);
      await handleRemoveBarcode(scannedValue);
    } else {
      beep(100, 520, 200);
      await createAndAddBarcode({ value: scannedValue, isScanned: true });
    }
  };

  const handleAddBarcode = async () => {
    if (!tempBarcode.trim()) return;
    setTempBarcode("");
    await createAndAddBarcode({ value: tempBarcode, isScanned: false });
  };

  const handleRemoveBarcode = async (scannedValue) => {
    const index = allBarcodes.findIndex((item) => item.value === scannedValue);
    if (index === -1) return;

    const barcodeToDelete = allBarcodes[index];
    const updatedBarcodes = allBarcodes.filter((_, i) => i !== index);
    setAllBarcodes(updatedBarcodes);

    try {
      await API.graphql(
        graphqlOperation(deleteBarcode, {
          input: { id: barcodeToDelete.id },
        })
      );

      if (inventoryItem.id && updatedBarcodes.length > 0) {
        await API.graphql(
          graphqlOperation(updateInventoryItem, {
            input: {
              id: inventoryItem.id,
              quantityOnHand: updatedBarcodes.length,
            },
          })
        );
      }
    } catch (err) {
      logError("BarcodeScanDialog:handleRemoveBarcode", err, notify);
    }
  };

  const toggleRemoveMode = () => {
    const newMode = !isRemoveMode;
    setIsRemoveMode(newMode);
    removeModeFlagRef.current = newMode;
  };

  const handleTempBarcodeChange = (e) => {
    const input = e.target.value;
    const alphanumericOnly = input.replace(/[^a-z0-9]/gi, "").toUpperCase();
    setTempBarcode(alphanumericOnly);
  };

  const handleClose = () => {
    setIsRemoveMode(false);
    removeModeFlagRef.current = false;
    onSave(allBarcodes.length);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      fullScreen={smDown}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box
        component="span"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        m={2}
      >
        <Typography variant="h6">
          {isRemoveMode ? "Scan to Remove" : "Scan to Add"}
          <Typography
            component="span"
            variant="subtitle2"
            style={{ marginLeft: 8 }}
          >
            ({allBarcodes.length} scanned)
          </Typography>
        </Typography>

        <CancelOutlinedIcon fontSize="large" onClick={handleClose} />
      </Box>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <QrBarcodeScanner onScanSuccess={handleSuccess} />
          </Grid>
          <Grid item xs={12} container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                label="Barcode"
                variant="outlined"
                fullWidth
                margin="dense"
                value={tempBarcode}
                onChange={handleTempBarcodeChange}
                disabled={isRemoveMode}
              />
            </Grid>
            <Grid item>
              {smUp ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddBarcode}
                  startIcon={<AddCircleIcon />}
                  disabled={isRemoveMode}
                >
                  ADD
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddBarcode}
                >
                  <AddCircleIcon />
                </Button>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.scrollableContainer}>
            {allBarcodes.map((item, index) => (
              <Grid
                key={index}
                item
                xs={12}
                container
                spacing={1}
                alignItems="center"
              >
                <Grid item xs>
                  <Typography>{item.value}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption">
                    In: {item.scannedIn}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    className={classes.removeIcon}
                    onClick={() => handleRemoveBarcode(item.value)}
                    color="secondary"
                  >
                    <DeleteIcon />
                  </Button>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          variant={isRemoveMode ? "contained" : "outlined"}
          color={isRemoveMode ? "secondary" : "default"}
          size="small"
          onClick={toggleRemoveMode}
          className={classes.scanButton}
        >
          {isRemoveMode ? "Cancel Remove Mode" : "Scan to Remove"}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="text" onClick={handleClose} color="default">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
